/**
 * taskRefineModel.ts
 * Pure helpers for the per-task "refine" control in the "Staff this job" view
 * (PHASE2-REFRAME.md §4). A person added to the job-week roster is present on
 * EVERY task of the source that week (the roster materializes task-level
 * presence rows). "Refine" narrows WHICH of that week's tasks the person is on,
 * without touching their week allocation % (the source-level share is unchanged
 * — refine only adds/removes per-task presence rows).
 *
 * No React / services — this module only computes; the hook does the I/O.
 */

import { weekOverlapsRange } from '../../../../utils/weekUtils';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';

/** 'YYYY-MM-DD' regardless of a trailing time component the API may include. */
function isoDay(d: string | null | undefined): string {
  return (d ?? '').slice(0, 10);
}

/** Does this task run at all during the given BST week (Saturday start)? */
export function taskRunsInWeek(task: TaskWithBookings, weekStart: string): boolean {
  if (task.StartDate && task.EndDate) {
    return weekOverlapsRange(weekStart, isoDay(task.StartDate), isoDay(task.EndDate));
  }
  // No task-level span → fall back to any active booking overlapping the week.
  return task.Bookings.some((b) => weekOverlapsRange(weekStart, isoDay(b.StartDate), isoDay(b.EndDate)));
}

/** The source's tasks running during a week (sorted by name for a stable list). */
export function tasksRunningInWeek(
  tasks: readonly TaskWithBookings[],
  weekStart: string,
): TaskWithBookings[] {
  return tasks
    .filter((t) => taskRunsInWeek(t, weekStart))
    .slice()
    .sort((a, b) => a.Name.localeCompare(b.Name));
}

/** The person's active presence rows on (task, week) — one per booking + a task-level row. */
export function presenceRowsForTaskWeek(
  rows: readonly TaskAssignee[] | undefined,
  userKey: string,
  weekStart: string,
): TaskAssignee[] {
  if (!rows) return [];
  const uk = userKey.toLowerCase();
  return rows.filter((r) => r.UserKey.toLowerCase() === uk && isoDay(r.WeekStart) === weekStart);
}

/** Is the person present on this task during the week? */
export function personPresentOnTaskWeek(
  rows: readonly TaskAssignee[] | undefined,
  userKey: string,
  weekStart: string,
): boolean {
  return presenceRowsForTaskWeek(rows, userKey, weekStart).length > 0;
}

/** The distinct skill levels (via a lookup) of everyone present on a task that week. */
export function presentLevelsOnTaskWeek(
  rows: readonly TaskAssignee[] | undefined,
  weekStart: string,
  levelFor: (userKey: string) => number | null | undefined,
): Array<number | null | undefined> {
  if (!rows) return [];
  const seen = new Set<string>();
  const out: Array<number | null | undefined> = [];
  for (const r of rows) {
    if (isoDay(r.WeekStart) !== weekStart) continue;
    const uk = r.UserKey.toLowerCase();
    if (seen.has(uk)) continue;
    seen.add(uk);
    out.push(levelFor(r.UserKey));
  }
  return out;
}

/** The refine write plan: from the currently-present task set → the desired one. */
export interface RefineWritePlan {
  /** Tasks to (re-)add the person's presence to. */
  toAdd: string[];
  /** Tasks to remove the person's presence from. */
  toRemove: string[];
}

/**
 * Pure reducer: given the tasks the person is currently present on and the set
 * they should be present on (the checked boxes), compute the presence writes.
 * Order is deterministic (sorted) so the outcome is testable.
 */
export function planRefineWrites(
  current: ReadonlySet<string>,
  desired: ReadonlySet<string>,
): RefineWritePlan {
  const toAdd: string[] = [];
  const toRemove: string[] = [];
  for (const key of desired) if (!current.has(key)) toAdd.push(key);
  for (const key of current) if (!desired.has(key)) toRemove.push(key);
  return { toAdd: toAdd.sort(), toRemove: toRemove.sort() };
}

/** Count of tasks the person is present on / total tasks running that week. */
export interface RefineSummary {
  onCount: number;
  totalCount: number;
  /** True when the person is on a subset (worth showing "3 of 5 tasks"). */
  isSubset: boolean;
  /** Advisory: a junior on one of the on-tasks lacks an L3+ mentor that week. */
  needsMentor: boolean;
}
